import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

export default function AuthPage({ mode = "login" }) {
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo = location.state?.from || "/";
  const { signIn, signUp, isAuthenticated, loading: authLoading } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);

  const isSignup = mode === "signup";

  // Single source of truth for the redirect — fires whenever isAuthenticated
  // becomes true. Avoids the race where signIn() resolves before the session
  // has propagated through Supabase's onAuthStateChange callback.
  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      navigate(redirectTo, { replace: true });
    }
  }, [authLoading, isAuthenticated, navigate, redirectTo]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setInfo(null);
    setBusy(true);
    try {
      if (isSignup) {
        await signUp(email, password);
        try {
          await signIn(email, password);
          // Don't navigate here — the useEffect above will handle it
          // once isAuthenticated flips true.
        } catch (_) {
          setInfo("Account created. Please check your email if confirmation is required, then sign in.");
          setBusy(false);
        }
      } else {
        await signIn(email, password);
        // Same — let the effect handle navigation
      }
    } catch (err) {
      setError(err?.message || "Authentication failed");
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2" data-testid="auth-page">
      <aside className="relative bg-ink-900 text-bone overflow-hidden hidden lg:flex flex-col justify-between p-12 xl:p-16">
        <div className="paper-grain absolute inset-0 opacity-50 pointer-events-none" />
        <Link to="/" className="relative inline-flex flex-col gap-3 z-10 group w-fit" data-testid="brand-mark">
          <img
            src="/brand-ad-mark.png"
            alt="Akshat Dani"
            className="h-20 xl:h-24 w-auto object-contain"
          />
          <span className="font-mono text-[11px] uppercase tracking-wide-3 text-vermilion group-hover:text-bone transition-colors">
            FCH&nbsp;/&nbsp;01 · The Framework Library
          </span>
        </Link>

        <div className="relative z-10 max-w-xl">
          <p className="font-mono text-[11px] uppercase tracking-wide-3 text-vermilion">
            // The reference library
          </p>
          <h2 className="mt-8 font-heading text-5xl xl:text-6xl font-semibold leading-[1.04] tracking-[-0.03em]">
            Strategic frameworks,
            <br />
            translated to the
            <br />
            language <em className="text-vermilion font-medium">of your client.</em>
          </h2>
          <p className="mt-8 max-w-md text-[15px] text-bone/70 leading-relaxed">
            A working reference of sixty strategic frameworks — recommended by problem, written for senior consultants.
          </p>
        </div>

        <p className="relative z-10 flex items-center gap-3 font-mono text-[10px] uppercase tracking-wide-3 text-bone/40">
          <img
            src="/brand-ad-mark.png"
            alt=""
            className="h-5 w-5 object-contain opacity-60"
          />
          © Akshat Dani · Framework Cheat Sheet Hub
        </p>
      </aside>

      <main className="flex flex-col bg-bone px-6 sm:px-12 md:px-16 lg:px-20 py-10 lg:py-16">
        <div className="flex items-center justify-between lg:justify-end">
          <Link
            to="/"
            className="lg:hidden font-mono text-[11px] uppercase tracking-wide-3 text-ink-900"
            data-testid="brand-mark-mobile"
          >
            FCH / 01
          </Link>
          <Link
            to={isSignup ? "/signin" : "/signup"}
            data-testid={isSignup ? "auth-link-signin" : "auth-link-signup"}
            className="font-mono text-[11px] uppercase tracking-wide-2 text-ink-500 hover:text-ink-900 transition-colors"
          >
            {isSignup ? "Have an account? Sign in →" : "New here? Create one →"}
          </Link>
        </div>

        <div className="flex-1 flex items-center">
          <div className="w-full max-w-md mx-auto py-12 lg:py-0">
            <p className="font-mono text-[11px] uppercase tracking-wide-3 text-vermilion">
              {isSignup ? "Open an account" : "Welcome back"}
            </p>
            <h1 className="mt-4 font-heading text-4xl md:text-5xl font-semibold text-ink-900 tracking-[-0.03em] leading-[1.04]">
              {isSignup ? "Begin your reference." : "Sign in to your hub."}
            </h1>
            <p className="mt-4 text-[15px] text-ink-600 leading-relaxed">
              {isSignup
                ? "Members receive unlimited access to all 1,500 framework recommendations and can bookmark the ones they cite most."
                : "Enter your credentials to continue your consultations."}
            </p>

            <form onSubmit={handleSubmit} className="mt-10 space-y-6" data-testid="auth-form">
              <div>
                <label htmlFor="email" className="block font-mono text-[10px] uppercase tracking-wide-3 text-ink-500 mb-3">
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  autoComplete="email"
                  data-testid="auth-email"
                  className="w-full bg-transparent border-0 border-b border-ink-300 px-1 py-2.5 text-base text-ink-900 placeholder:text-ink-400 focus:outline-none focus:border-vermilion focus:border-b-2 transition-colors"
                />
              </div>
              <div>
                <label htmlFor="password" className="block font-mono text-[10px] uppercase tracking-wide-3 text-ink-500 mb-3">
                  Password
                </label>
                <input
                  id="password"
                  type="password"
                  required
                  minLength={6}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  autoComplete={isSignup ? "new-password" : "current-password"}
                  data-testid="auth-password"
                  className="w-full bg-transparent border-0 border-b border-ink-300 px-1 py-2.5 text-base text-ink-900 placeholder:text-ink-400 focus:outline-none focus:border-vermilion focus:border-b-2 transition-colors"
                />
              </div>

              {error && (
                <div className="border-l-2 border-vermilion-deep pl-4 py-2 text-sm text-ink-900" data-testid="auth-error">
                  {error}
                </div>
              )}
              {info && (
                <div className="border-l-2 border-ink-300 pl-4 py-2 text-sm text-ink-700" data-testid="auth-info">
                  {info}
                </div>
              )}

              <button
                type="submit"
                disabled={busy}
                data-testid="auth-submit"
                className="w-full inline-flex items-center justify-center gap-3 px-7 py-4 bg-vermilion text-bone hover:bg-ink-900 hover:text-bone font-mono text-[12px] uppercase tracking-wide-2 transition-colors disabled:opacity-60"
              >
                {busy ? "Working…" : isSignup ? "Create account" : "Sign in"}
                {!busy && <ArrowRight size={14} />}
              </button>
            </form>
          </div>
        </div>

        <p className="font-mono text-[10px] uppercase tracking-wide-3 text-ink-400 mt-8 text-center lg:text-right">
          60 frameworks · 1,500 documented problems
        </p>
      </main>
    </div>
  );
}
